import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../Services/axiosInstance';
import { useAuth } from '../contexts/AuthContext';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardIconHeader,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { cn } from '../lib/utils';
import {
  MessageCircle,
  Send,
  Bot,
  User,
  CheckSquare,
  FolderKanban,
  Users,
  Database,
  UserCircle,
  BarChart3,
  Lightbulb,
  AlertCircle,
  Loader2,
  Sparkles,
  ArrowRight,
} from 'lucide-react';

// ── Types ───────────────────────────────────────────────────────────

interface ChatQueryResponse {
  intent: string;
  widgetType: string;
  message: string;
  data?: any;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  widgetType?: string;
  intent?: string;
  data?: any;
}

// ── Suggested prompts ───────────────────────────────────────────────

const SUGGESTED_PROMPTS = [
  { label: 'Show my tasks', icon: <CheckSquare size={14} /> },
  { label: 'What projects do I have?', icon: <FolderKanban size={14} /> },
  { label: 'How many users are there?', icon: <Users size={14} /> },
  { label: 'Give me an overview', icon: <BarChart3 size={14} /> },
  { label: 'Who am I?', icon: <UserCircle size={14} /> },
  { label: 'How many subscriptions?', icon: <Database size={14} /> },
];

// ── Widget renderers ────────────────────────────────────────────────

const TasksWidget: React.FC<{ data: any; navigate: (path: string) => void }> = ({ data, navigate }) => {
  const items = data?.items ?? [];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline" className="gap-1.5">
          <span className="h-2 w-2 rounded-full bg-slate-400" />
          {data?.todo ?? 0} to do
        </Badge>
        <Badge variant="outline" className="gap-1.5">
          <span className="h-2 w-2 rounded-full bg-blue-500" />
          {data?.inProgress ?? 0} in progress
        </Badge>
        {data?.inReview !== undefined && (
          <Badge variant="outline" className="gap-1.5">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            {data.inReview} in review
          </Badge>
        )}
        <Badge variant="outline" className="gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          {data?.done ?? 0} done
        </Badge>
        {data?.overdueCount > 0 && (
          <Badge variant="destructive" className="gap-1.5">
            <AlertCircle size={12} />
            {data.overdueCount} overdue
          </Badge>
        )}
      </div>
      {items.length > 0 && (
        <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
          {items.map((task: any) => (
            <div
              key={task.id}
              className="flex items-center justify-between rounded-lg border p-2.5 hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => navigate(`/projects/${task.projectId}`)}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{task.title}</p>
                <p className="text-xs text-muted-foreground">{task.projectName}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                <Badge variant={task.priority === 'High' || task.priority === 'Urgent' ? 'destructive' : 'secondary'} className="text-[11px]">
                  {task.priority}
                </Badge>
                <Badge variant="outline" className="text-[11px]">{task.status}</Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProjectsWidget: React.FC<{ data: any; navigate: (path: string) => void }> = ({ data, navigate }) => {
  const items = data?.items ?? [];
  return (
    <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
      {items.map((project: any) => (
        <div
          key={project.id}
          className="flex items-center justify-between rounded-lg border p-2.5 hover:bg-muted/50 cursor-pointer transition-colors"
          onClick={() => navigate(`/projects/${project.id}`)}
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{project.name}</p>
            {project.description && (
              <p className="text-xs text-muted-foreground truncate">{project.description}</p>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0 ml-2">
            <Badge variant="secondary" className="text-[11px]">{project.taskCount} tasks</Badge>
            <Badge variant="outline" className="text-[11px]">{project.memberCount} members</Badge>
          </div>
        </div>
      ))}
    </div>
  );
};

const CountWidget: React.FC<{ data: any; icon: React.ReactNode; label: string }> = ({ data, icon, label }) => (
  <div className="flex items-center gap-4">
    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
      {icon}
    </div>
    <div>
      <div className="text-3xl font-bold">{data?.count ?? '—'}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  </div>
);

const ProfileWidget: React.FC<{ data: any }> = ({ data }) => (
  <div className="flex items-center gap-4">
    <Avatar className="h-12 w-12">
      <AvatarFallback className="bg-primary/10 text-primary font-medium">
        {data?.name?.charAt(0)?.toUpperCase() ?? '?'}
      </AvatarFallback>
    </Avatar>
    <div className="space-y-0.5">
      <div className="text-lg font-semibold">{data?.name ?? '—'}</div>
      <div className="text-sm text-muted-foreground">{data?.email}</div>
      {data?.role && <Badge variant="secondary" className="text-xs mt-1">{data.role}</Badge>}
      {data?.organisation && (
        <span className="text-xs text-muted-foreground ml-2">{data.organisation}</span>
      )}
    </div>
  </div>
);

const StatisticsWidget: React.FC<{ data: any }> = ({ data }) => (
  <div className="grid grid-cols-2 gap-3">
    <div className="rounded-lg border p-3 text-center">
      <div className="text-2xl font-bold">{data?.usersCount ?? '—'}</div>
      <div className="text-xs text-muted-foreground">Users</div>
    </div>
    <div className="rounded-lg border p-3 text-center">
      <div className="text-2xl font-bold">{data?.subscriptionsCount ?? '—'}</div>
      <div className="text-xs text-muted-foreground">Subscriptions</div>
    </div>
    <div className="rounded-lg border p-3 text-center">
      <div className="text-2xl font-bold">{data?.tasksCount ?? '—'}</div>
      <div className="text-xs text-muted-foreground">Tasks</div>
    </div>
    <div className="rounded-lg border p-3 text-center">
      <div className="text-2xl font-bold">{data?.projectsCount ?? '—'}</div>
      <div className="text-xs text-muted-foreground">Projects</div>
    </div>
    {data?.taskBreakdown && (
      <div className="col-span-2 flex gap-2 justify-center">
        <Badge variant="outline" className="gap-1.5 text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
          {data.taskBreakdown.todo} to do
        </Badge>
        <Badge variant="outline" className="gap-1.5 text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
          {data.taskBreakdown.inProgress} in progress
        </Badge>
        <Badge variant="outline" className="gap-1.5 text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {data.taskBreakdown.done} done
        </Badge>
      </div>
    )}
  </div>
);

const SuggestionsWidget: React.FC<{ data: any; onSuggestionClick: (text: string) => void }> = ({ data, onSuggestionClick }) => (
  <div className="flex flex-wrap gap-2">
    {(data?.suggestions ?? []).map((suggestion: string, i: number) => (
      <Button
        key={i}
        variant="outline"
        size="sm"
        className="gap-1.5 text-xs"
        onClick={() => onSuggestionClick(suggestion)}
      >
        <Lightbulb size={12} />
        {suggestion}
      </Button>
    ))}
  </div>
);

// ── Widget type to renderer map ─────────────────────────────────────

function renderWidget(
  msg: ChatMessage,
  navigate: (path: string) => void,
  onSuggestionClick: (text: string) => void,
) {
  const { widgetType, data } = msg;
  if (!widgetType || !data) return null;

  switch (widgetType) {
    case 'tasks-overview':
    case 'tasks-overdue':
      return <TasksWidget data={data} navigate={navigate} />;
    case 'projects-overview':
      return <ProjectsWidget data={data} navigate={navigate} />;
    case 'users-count':
      return <CountWidget data={data} icon={<Users size={22} />} label="Total users" />;
    case 'subscriptions-count':
      return <CountWidget data={data} icon={<Database size={22} />} label="Active subscriptions" />;
    case 'profile-card':
      return <ProfileWidget data={data} />;
    case 'statistics-dashboard':
      return <StatisticsWidget data={data} />;
    case 'suggestions':
      return <SuggestionsWidget data={data} onSuggestionClick={onSuggestionClick} />;
    case 'error':
      return (
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle size={16} />
          <span className="text-sm">An error occurred</span>
        </div>
      );
    default:
      return null;
  }
}

// ── Intent badge ────────────────────────────────────────────────────

const intentMeta: Record<string, { icon: React.ReactNode; color: string }> = {
  tasks:         { icon: <CheckSquare size={12} />, color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' },
  projects:      { icon: <FolderKanban size={12} />, color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  users:         { icon: <Users size={12} />, color: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  subscriptions: { icon: <Database size={12} />, color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  profile:       { icon: <UserCircle size={12} />, color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  statistics:    { icon: <BarChart3 size={12} />, color: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400' },
  help:          { icon: <Lightbulb size={12} />, color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400' },
  error:         { icon: <AlertCircle size={12} />, color: 'bg-red-500/10 text-red-600 dark:text-red-400' },
};

// ── Main Component ──────────────────────────────────────────────────

const NavigatorChat: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }

    try {
      const res = await axiosInstance.post<ChatQueryResponse>('/api/Chat/query', {
        message: trimmed,
      });

      const response = res.data;
      const assistantMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
        widgetType: response.widgetType,
        intent: response.intent,
        data: response.data,
      };

      setMessages(prev => [...prev, assistantMsg]);
    } catch {
      const errorMsg: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, I couldn\'t process your request. Please try again.',
        timestamp: new Date(),
        widgetType: 'error',
        intent: 'error',
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  }, [isLoading]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleSuggestionClick = useCallback((text: string) => {
    sendMessage(text);
  }, [sendMessage]);

  const userName = auth.user?.name ?? auth.user?.email?.split('@')[0] ?? 'You';

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)]">
      {/* Header */}
      <Card className="shrink-0">
        <CardIconHeader icon={<MessageCircle size={22} className="text-primary" />}>
          <CardTitle className="flex items-center gap-2">
            Navigator Chat
            <Badge variant="secondary" className="text-[10px] font-normal gap-1">
              <Sparkles size={10} />
              Agents Builder
            </Badge>
          </CardTitle>
          <CardDescription>
            Ask questions about your tasks, projects, users, and more.
            Responses are rendered as interactive widgets — just like a Navigator MFE.
          </CardDescription>
        </CardIconHeader>
      </Card>

      {/* Chat Area */}
      <div className="flex-1 min-h-0 mt-4 rounded-xl border bg-muted/20 flex flex-col overflow-hidden">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full gap-6 py-12">
              <div className="rounded-full bg-primary/10 p-4">
                <Bot size={32} className="text-primary" />
              </div>
              <div className="text-center space-y-2 max-w-md">
                <h3 className="text-lg font-semibold">Welcome to Navigator Chat</h3>
                <p className="text-sm text-muted-foreground">
                  I can help you find information about your tasks, projects, users,
                  subscriptions, and profile. Try one of the suggestions below or type your own question.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {SUGGESTED_PROMPTS.map((prompt, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs hover:bg-primary/5 hover:border-primary/30 transition-colors"
                    onClick={() => sendMessage(prompt.label)}
                  >
                    {prompt.icon}
                    {prompt.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                "flex gap-3 animate-fade-in-up",
                msg.role === 'user' ? "flex-row-reverse" : "flex-row"
              )}
            >
              {/* Avatar */}
              <Avatar className="h-8 w-8 shrink-0 mt-0.5">
                <AvatarFallback
                  className={cn(
                    "text-xs font-medium",
                    msg.role === 'user'
                      ? "bg-primary/10 text-primary"
                      : "bg-violet-500/10 text-violet-600 dark:text-violet-400"
                  )}
                >
                  {msg.role === 'user' ? <User size={14} /> : <Bot size={14} />}
                </AvatarFallback>
              </Avatar>

              {/* Message bubble */}
              <div
                className={cn(
                  "flex flex-col gap-2 max-w-[80%]",
                  msg.role === 'user' ? "items-end" : "items-start"
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">
                    {msg.role === 'user' ? userName : 'Evo Assistant'}
                  </span>
                  {msg.intent && intentMeta[msg.intent] && (
                    <span className={cn(
                      "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium",
                      intentMeta[msg.intent].color
                    )}>
                      {intentMeta[msg.intent].icon}
                      {msg.intent}
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground/60">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>

                <div
                  className={cn(
                    "rounded-xl px-4 py-3 text-sm",
                    msg.role === 'user'
                      ? "bg-primary text-primary-foreground rounded-br-md"
                      : "bg-card border shadow-sm rounded-bl-md"
                  )}
                >
                  <p>{msg.content}</p>
                </div>

                {/* Widget rendering */}
                {msg.role === 'assistant' && msg.widgetType && msg.data && (
                  <div className="w-full rounded-xl bg-card border shadow-sm p-4">
                    {renderWidget(msg, navigate, handleSuggestionClick)}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex gap-3 animate-fade-in-up">
              <Avatar className="h-8 w-8 shrink-0 mt-0.5">
                <AvatarFallback className="bg-violet-500/10 text-violet-600 dark:text-violet-400">
                  <Bot size={14} />
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-muted-foreground">Evo Assistant</span>
                <div className="rounded-xl bg-card border shadow-sm px-4 py-3 rounded-bl-md">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 size={14} className="animate-spin" />
                    Thinking...
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="shrink-0 border-t bg-background/80 backdrop-blur-sm p-4">
          <div className="flex items-end gap-2 max-w-3xl mx-auto">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your tasks, projects, users..."
                className={cn(
                  "w-full resize-none rounded-xl border bg-background px-4 py-3 pr-12 text-sm",
                  "placeholder:text-muted-foreground/60",
                  "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40",
                  "min-h-[44px] max-h-[120px]",
                  "transition-all duration-200"
                )}
                rows={1}
                disabled={isLoading}
              />
              <Button
                size="icon"
                className="absolute right-2 bottom-1.5 h-8 w-8 rounded-lg"
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isLoading}
              >
                {isLoading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
              </Button>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground/50 text-center mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>

      {/* Agents Builder info card */}
      <Card className="shrink-0 mt-4 border-dashed">
        <CardContent className="py-3 px-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-violet-500/10 p-2 shrink-0">
              <Sparkles size={16} className="text-violet-500" />
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">Powered by Agents Builder</p>
              <p>
                This chat endpoint (<code className="rounded bg-muted px-1 py-0.5 font-mono">POST /api/chat/query</code>)
                is discoverable via <code className="rounded bg-muted px-1 py-0.5 font-mono">GET /api/chat/capabilities</code> and
                can be connected as an MCP tool in Agents Builder to power Navigator Chat conversations with real product data.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 gap-1 text-xs"
              onClick={() => window.open(`${axiosInstance.defaults.baseURL}/api/Chat/capabilities`, '_blank')}
            >
              View API
              <ArrowRight size={12} />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NavigatorChat;
